<template lang="pug">
    .form__container
        .form
            .form__title
                slot(name="title")
            form.form__body(
                action="javascript://"
                @submit="submitForm"
            )
                template(v-for="(translation, index) in courseCopy.translations")
                    .form__field(:key="`name_${index}`")
                        .form__label
                            | {{ $i18n.t('course.course_form_addName') + $i18n.t(`course.course_form_${translation.locale}`) }}
                        .form__field-body
                            cs-input.form__input(
                                :error="errors.has(`name_${translation.locale}`)"
                                type="text"
                                :name="`name_${translation.locale}`"
                                v-validate="'required'"
                                v-model="translation.name"
                                :data-vv-as="$i18n.t('course.course_form_name').toLowerCase()"
                            )
                            help-text(
                                v-if="errors.has(`name_${translation.locale}`)"
                                :key="`name_help_text${index}`"
                                :error="errors.has(`name_${translation.locale}`)"
                            )
                                | {{ errors.first(`name_${translation.locale}`)}}
                    .form__field(:key="`description_${index}`")
                        .form__label
                            | {{ $i18n.t('course.course_form_addDescription') + $i18n.t(`course.course_form_${translation.locale}`) }}
                        .form__field-body
                            textarea.form__textarea(
                                :class="{'is-danger': errors.has(`description_${translation.locale}`)}"
                                v-model="translation.description"
                                :name="`description_${translation.locale}`"
                                v-validate="'required'"
                                :data-vv-as="$i18n.t('course.course_form_description').toLowerCase()"
                            )
                            help-text(
                                v-if="errors.has(`description_${translation.locale}`)"
                                :key="`description_help_text${index}`"
                                :error="errors.has(`description_${translation.locale}`)"
                            )
                                | {{ errors.first(`description_${translation.locale}`)}}
                    divider
                .form__field
                    .form__label
                        | {{ $i18n.t('course.course_form_status') }}
                    .form__field-body
                        cs-multiselect(
                            :multiple="false"
                            :options="statuses"
                            :show-labels="false"
                            :custom-label="getStatusName"
                            :allow-empty="false"
                            placeholder=""
                            v-model="courseCopy.status"
                            :searchable="false"
                        )
                divider
                .form__field(v-for="index in amountOfGames")
                    .form__label
                        span(v-if="index === 1")
                            | {{ $i18n.t('course.course_form_chooseGames') }}
                    .form__field-body
                        cs-multiselect(
                            :multiple="false"
                            :options="availableGames"
                            :show-labels="false"
                            :allow-empty="false"
                            :placeholder="$i18n.t('course.course_form_chooseGamesPlaceholder')"
                            label="name"
                            v-model="courseCopy.games[index - 1]"
                            :error="errors.has('games')"
                        )
                        help-text(v-if="errors.has('games')" :error="errors.has('games')")
                            | {{ errors.first('games')}}
                divider
                .form__field
                    games-list.form__games(
                        :games='courseCopy.games',
                        without-moves
                        @editGame='editGame',
                        @remove='removeGame',
                    )
                .form__buttons
                    cs-button.form__button(
                        color="blue"
                        type="submit"
                        :loading="isLoading"
                    )
                        slot(name="button")
                    cs-button.form__button(
                        color="blue"
                        pattern="primary-outline"
                        @click.prevent="$router.back()"
                    )
                        | {{ $i18n.t('common.common_button_cancel') }}
</template>

<script>
    import Trainer from '../../config/trainer';
    import TrainerUtilities from '../../utils/trainer/trainer';

    const AdditionAndSubtraction = () => import('../games/games-previews/AddSubPreview.vue');
    const FlashCards = () => import('../games/games-previews/FlashCardsPreview.vue');
    const MultiplicationAndDivision = () => import('../games/games-previews/MultDivPreview.vue');
    const AssembleTheNumber = () => import('../games/games-previews/AssembleTheNumberPreview.vue');
    const SequenceOfNumbers = () => import('../games/games-previews/SequenceOfNumbersPreview.vue');
    const Equation = () => import('../games/games-previews/EquationPreview.vue');
    const Factorization = () => import('../games/games-previews/FactorizationPreview.vue');
    const EquationMultiplication = () => import('../games/games-previews/EquationMultiplicationPreview.vue');
    const Anzan = () => import('../games/games-previews/AnzanPreview.vue');
    const Puzzle = () => import('../games/games-previews/PuzzlePreview.vue');
    const Couples = () => import('../games/games-previews/CouplesPreview.vue');
    const Chains = () => import('../games/games-previews/ChainsPreview.vue');
    const Schulte = () => import('../games/games-previews/SchultePreview.vue');
    const Scanner = () => import('../games/games-previews/ScannerPreview.vue');
    const Shutter = () => import('../games/games-previews/ShutterPreview.vue');
    const ShutterReadingEn = () => import('../games/games-previews/ShutterReadingEnPreview.vue');
    const ShutterRussianLanguage = () => import('../games/games-previews/ShutterRussianLanguagePreview.vue');
    const WordsField = () => import('../games/games-previews/WordsFieldPreview.vue');
    const WordsFieldReadingEn = () => import('../games/games-previews/WordsFieldReadingEnPreview.vue');
    const Anagrams = () => import('../games/games-previews/AnagramsPreview.vue');
    const Texts = () => import('../games/games-previews/TextsPreview.vue');
    const LettersField = () => import('../games/games-previews/LettersFieldPreview.vue');
    const CatchWord = () => import('../games/games-previews/CatchWordPreview.vue');
    const CatchWordReadingEn = () => import('../games/games-previews/CatchWordReadingEnPreview.vue');
    const VisionField = () => import('../games/games-previews/VisionFieldPreview.vue');
    const VisionFieldReadingEn = () => import('../games/games-previews/VisionFieldReadingEnPreview.vue');
    const Matrix = () => import('../games/games-previews/MatrixPreview.vue');
    const Navigator = () => import('../games/games-previews/NavigatorPreview.vue');
    const Presentations = () => import('../games/games-previews/PresentationsPreview.vue');
    const Synonyms = () => import('../games/games-previews/SynonymsPreview.vue');
    const SynonymsReadingEn = () => import('../games/games-previews/SynonymsReadingEnPreview.vue');
    const SynonymsRussianLanguage = () => import('../games/games-previews/SynonymsRussianLanguagePreview.vue');
    const Antonyms = () => import('../games/games-previews/AntonymsPreview.vue');
    const AntonymsRussianLanguage = () => import('../games/games-previews/AntonymsRussianLanguagePreview.vue');
    const Search = () => import('../games/games-previews/Search.vue');
    const Classifications = () => import('../games/games-previews/Classifications.vue');
    const SoundCouples = () => import('../games/games-previews/SoundCouples.vue');
    const FourthOne = () => import('../games/games-previews/FourthOne.vue');
    const DigitCouples = () => import('../games/games-previews/DigitCouplesPreview.vue');
    const Memory = () => import('../games/games-previews/MemoryPreview.vue');
    const Memorize = () => import('../games/games-previews/MemorizePreview.vue');
    const KeenEye = () => import('../games/games-previews/KeenEyePreview.vue');
    const NumericalNetwork = () => import('../games/games-previews/NumericalNetworkPreview.vue');
    const Expressions = () => import('../games/games-previews/ExpressionsPreview.vue');
    const RootsAndDegrees = () => import('../games/games-previews/RootsAndDegreesPreview');
    const Loner = () => import('../games/games-previews/LonerPreview.vue');
    const Associations = () => import('../games/games-previews/AssociationsPreview.vue');
    const WhoIsWho = () => import('../games/games-previews/WhoIsWhoPreview.vue');
    const VocabularyWords = () => import('../games/games-previews/VocabularyWordsPreview.vue');
    const WordDrum = () => import('../games/games-previews/WordDrumPreview.vue');
    const VocabularyMemoRus = () => import('../games/games-previews/VocabularyMemoRusPreview.vue');
    const CosmoTour = () => import('../games/games-previews/CosmoTourPreview.vue');
    const DivideItRight = () => import('../games/games-previews/DivideItRightPreview.vue');
    const ChooseItRight = () => import('../games/games-previews/ChooseItRightPreview.vue');
    const DivideUnstressedVowels = () => import('../games/games-previews/DivideUnstressedVowelsPreview.vue');
    const VocabularyWordsRus = () => import('../games/games-previews/VocabularyWordsRusPreview.vue');
    const LettersSoundsSyllables = () => import('../games/games-previews/LettersSoundsSyllablesPreview.vue');
    const TransferSyllables = () => import('../games/games-previews/TransferSyllablesPreview.vue');
    const TransferSyllablesSchoolPreparing = () => import('../games/games-previews/TransferSyllablesSchoolPreparingPreview.vue');
    const HardSoftConsonants = () => import('../games/games-previews/HardSoftConsonantsPreview.vue');
    const HardSoftConsonantsSchoolPreparing = () => import('../games/games-previews/HardSoftConsonantsSchoolPreparingPreview.vue');
    const TranscriptionInWord = () => import('../games/games-previews/TranscriptionInWordPreview.vue');
    const VoicedAndDeafConsonants = () => import('../games/games-previews/VoicedAndDeafConsonantsPreview.vue');
    const PartsOfSpeech = () => import('../games/games-previews/PartsOfSpeechPreview.vue');
    const ParsingSentencePartsSpeech = () => import('../games/games-previews/ParsingSentencePartsSpeechPreview.vue');
    const SortOneMany = () => import('../games/games-previews/SortOneManyPreview.vue');
    const GenericWords = () => import('../games/games-previews/GenericWordsPreview.vue');
    const VerbTenses = () => import('../games/games-previews/VerbTensesPreview.vue');
    const PunctuationMarks = () => import('../games/games-previews/PunctuationMarksPreview.vue');
    const Alphabet = () => import('../games/games-previews/AlphabetPreview.vue');
    const ConnectThePuzzles = () => import('../games/games-previews/ConnectThePuzzlesPreview.vue');
    const Orthograph = () => import('../games/games-previews/OrthographPreview.vue');
    const WordStructure = () => import('../games/games-previews/WordStructurePreview.vue');
    const SentenceParsing = () => import('../games/games-previews/SentenceParsingPreview.vue');

    import GetType from '../../utils/trainer/getType';
    import _ from 'lodash';
    import { mapState } from 'vuex';
    import GamesList from '../games/GamesList.vue';

    export default {
        data() {
            return {
                courseCopy: {},
                statuses: ['active', 'archived'],
                statusNames: {
                    active: this.$i18n.t('common.common_status_active'),
                    archived: this.$i18n.t('common.common_status_archived'),
                },
                isLoading: false,
                games: Trainer.games,
                possibleGamesForPuzzle: [
                    'Anzan',
                    'Equation',
                    'InteractiveFlashCards',
                    'AdditionAndSubtraction',
                    'AdditionAndSubtractionWithTime',
                    'MultiplicationAndDivision',
                ],
                allExtraOptions: {
                    x2: true,
                    motion: true,
                    pictures: true,
                    music: true,
                    voiceOver: true,
                },
            };
        },
        mixins: [TrainerUtilities, GetType],
        components: {
            GamesList,
            WordsFieldReadingEn,
            AdditionAndSubtraction,
            FlashCards,
            MultiplicationAndDivision,
            AssembleTheNumber,
            SequenceOfNumbers,
            Equation,
            Factorization,
            EquationMultiplication,
            Anzan,
            Puzzle,
            Couples,
            Chains,
            Schulte,
            Expressions,
            WordsField,
            Scanner,
            Shutter,
            ShutterRussianLanguage,
            ShutterReadingEn,
            Anagrams,
            Texts,
            LettersField,
            CatchWord,
            CatchWordReadingEn,
            VisionField,
            VisionFieldReadingEn,
            Matrix,
            Memorize,
            KeenEye,
            NumericalNetwork,
            Navigator,
            Presentations,
            Synonyms,
            SynonymsReadingEn,
            SynonymsRussianLanguage,
            Antonyms,
            AntonymsRussianLanguage,
            Search,
            Classifications,
            SoundCouples,
            FourthOne,
            RootsAndDegrees,
            Loner,
            DigitCouples,
            Memory,
            Associations,
            WhoIsWho,
            VocabularyWords,
            WordDrum,
            VocabularyMemoRus,
            CosmoTour,
            TransferSyllables,
            TransferSyllablesSchoolPreparing,
            TranscriptionInWord,
            Alphabet,
            ConnectThePuzzles,
            DivideItRight,
            ChooseItRight,
            DivideUnstressedVowels,
            WordStructure,
            SentenceParsing,
            VocabularyWordsRus,
            LettersSoundsSyllables,
            HardSoftConsonants,
            HardSoftConsonantsSchoolPreparing,
            VoicedAndDeafConsonants,
            PartsOfSpeech,
            ParsingSentencePartsSpeech,
            GenericWords,
            VerbTenses,
            SortOneMany,
            PunctuationMarks,
            Orthograph,
        },
        props: {
            course: Object,
            submit: Function,
        },
        created() {
            if (!this.homework) {
                this.courseCopy = _.cloneDeep(this.course);
                this.addGameLabels();
            }
            this.games.forEach(game => {
                if (game.parameters.default.extraOptions) {
                    game.parameters.default.extraOptions = this.allExtraOptions;
                }
            });
        },
        watch: {
            'courseCopy.games': {
                handler(newGames) {
                    if (newGames) {
                        if (newGames.length) {
                            this.errors.remove('games');
                        }
                        this.checkAvailableGamesForPuzzle();
                    }
                },
                immediate: true,
            },
            homework: {
                handler(newHomework) {
                    if (newHomework) {
                        this.courseCopy = newHomework;
                    }
                },
                deep: true,
                immediate: true,
            },
        },
        methods: {
            getStatusName(status) {
                return this.statusNames[status];
            },
            submitForm() {
                this.$validator
                    .validateAll()
                    .then(isPassedValidation => {
                        if (!this.courseCopy.games.length) {
                            this.errors.add('games', this.$i18n.t('course.course_form_chooseGames'));
                            return;
                        }
                        if (isPassedValidation) {
                            this.isLoading = true;
                            this.courseCopy.type = this.courseCopy.games[0].courseType;
                            delete this.courseCopy.grades;
                            this.submit(this.courseCopy)
                                .then(() => {
                                    this.isLoading = false;
                                })
                                .catch(err => {
                                    if (err.response.data.errors) {
                                        let errors = err.response.data.errors;
                                        Object.keys(errors).forEach(field => {
                                            errors[field].forEach(val => {
                                                this.errors.add(field, val);
                                            });
                                        });
                                    }
                                    this.isLoading = false;
                                });
                        }
                    })
                    .catch(() => {
                        this.$toasted.error(this.$i18n.t('errors.fill_form'));
                    });
            },
            editGame(index) {
                this.$store.dispatch('setHomework', this.courseCopy);
                if (this.courseCopy.games[index].slug === 'MultiplicationAndDivision') {
                    this.$router.push({
                        name: 'create:game',
                        params: { id: index, gameMode: 'course' },
                        query: { slug: 'MultiplicationAndDivisionCourse', courseSettings: true },
                    });
                } else {
                    this.$router.push({
                        name: 'create:game',
                        params: { id: index, gameMode: 'course' },
                        query: { slug: this.courseCopy.games[index].slug, courseSettings: true },
                    });
                }
            },
            addGameLabels() {
                this.courseCopy.games.forEach(game => (game.name = this.getGameName(game)));
                this.games.forEach(game => (game.name = this.getGameName(game)));
            },
            checkAvailableGamesForPuzzle() {
                let gameNames = [];
                this.courseCopy.games.forEach(game => {
                    gameNames.push(game.slug);
                });
                let puzzleIndex = _.findIndex(this.games, game => game.slug === 'Puzzle');
                let disablePuzzle = !this.possibleGamesForPuzzle.some(name => gameNames.includes(name));
                if (puzzleIndex !== -1) {
                    this.games[puzzleIndex].$isDisabled = disablePuzzle;
                } else if (disablePuzzle) {
                    let index = _.findIndex(this.courseCopy.games, game => {
                        return game.slug === 'Puzzle';
                    });
                    this.removeGame(index);
                }
            },
        },
        computed: {
            ...mapState({
                homework: state => state.trainer.homework,
                user: state => state.auth.user,
            }),
            availableGames() {
                let result = this.games.filter(({ isBonusGame }) => !isBonusGame);
                if (this.courseCopy.games.length) {
                    const currentCourseType = this.courseCopy.games[0].courseType;
                    const slugsCourseCopySet = new Set();
                    this.courseCopy.games.forEach(({ courseType, slug }) => {
                        currentCourseType === courseType && slugsCourseCopySet.add(slug);
                    });
                    result = result.filter(
                        ({ courseType, slug }) => courseType === currentCourseType && !slugsCourseCopySet.has(slug)
                    );
                }
                return result;
            },
            amountOfGames() {
                if (this.availableGames.length) {
                    return this.courseCopy.games.length + 1;
                }
                return this.courseCopy.games.length;
            },
        },
    };
</script>

<style lang="scss" scoped>
    .form__container {
        margin: 0;
        height: 100%;
        background-color: $x-color-white;
        border-radius: $border-radius-20;
        box-sizing: border-box;
        @include media($md) {
            padding: 30px;
        }
    }

    .form {
        margin: 0;
        padding: 30px 20px;
        height: 100%;
        background-color: $hsl-color-gray-98;
        border-radius: $border-radius-20;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        flex-direction: column;
        @include media($md) {
            padding: 30px;
        }
    }

    .form__title {
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        margin-bottom: 25px;
    }

    .form__body {
        width: 100%;
        box-sizing: border-box;
        @include media($lg) {
            padding: 0 4.5%;
        }
    }

    .form__field {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        @include media($md) {
            padding: 0 2.5px;
            flex-direction: row;
            align-items: center;
        }
        @include media($lg) {
            padding: 0 10%;
        }
        @include media($xlg) {
            padding: 0 13%;
        }
    }

    .form__games {
        margin: 0 0 0 auto;
        width: 100%;
    }

    .form__input {
        margin-bottom: 0;
    }

    .form__label {
        min-width: 0;
        font-size: 16px;
        line-height: 19px;
        margin-right: 20px;
        margin-bottom: 0.5rem;
        @include media($md) {
            width: 8em;
            min-width: 8em;
            margin-bottom: 0;
            text-align: right;
        }
        @include media($xlg) {
            width: 13em;
            min-width: 13em;
        }
    }

    .form__label--image {
        @include media($md) {
            padding-top: 2.5em;
        }
    }

    .form__field-body {
        flex-grow: 5;
    }

    .form__field-body--bold {
        font-weight: 500;
    }

    .form__buttons {
        margin-top: 30px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        @include media($md) {
            flex-direction: row;
        }
    }

    .form__button {
        margin: 10px 50px;
        @include media($md) {
            margin: 0 10px;
        }
    }

    .warning {
        display: flex;
        align-items: center;
        font-size: $font-size-16;
        line-height: $font-size-16;
    }

    .warning__icon {
        color: $hsl-color-red-62;
        margin-right: 8px;
    }

    .form__textarea {
        box-sizing: border-box;

        outline: none;

        color: $x-color-black;

        width: 100%;

        border-radius: $border-radius-5;
        border: 1px solid $hsl-color-gray-74;

        font-weight: normal;
        font-size: $font-size-16;
        line-height: $font-size-19;

        //@include inset-squish-space($space-small);

        padding: 12px 15px;

        @include stack-space($space-x-small);

        &:hover {
            border-color: $hsl-color-blue-56;
        }

        &:focus {
            border-color: $hsl-color-blue-56;
        }

        &:active {
            border-color: $hsl-color-blue-56;
        }

        &::placeholder {
            font-weight: lighter;
        }

        align-items: center;
        height: 2.25em;
        justify-content: flex-start;
        position: relative;
        vertical-align: top;
        background-color: #fff;
        box-shadow: inset 0 1px 2px hsla(0, 0%, 4%, 0.1);
        max-width: 100%;

        display: block;
        min-width: 100%;

        word-wrap: break-word;
        resize: none;
        max-height: 200px;
        min-height: 180px;

        overflow: hidden;
        overflow-wrap: break-word;
    }

    .game-preview {
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .game-preview__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        @include stack-space($space-small);
    }

    .game-preview__title {
        display: flex;
        align-items: center;
    }

    .game-preview__image {
        vertical-align: middle;
        margin-bottom: 12px;
        &:last-child {
            margin-right: 0;
        }
        @include media($sm) {
            margin: 0 12px 0 0;
            &:last-child {
                margin: 0;
            }
        }
    }

    .game-preview__parameters {
        margin-bottom: 12px;
        &:last-child {
            margin-right: 0;
        }
    }

    .game-preview__icon {
        color: $hsl-color-green-58;
        background-color: transparent;

        &:hover {
            color: $hsl-color-red-62;
        }
    }

    .game-preview__button {
        display: flex;
        align-items: center;
    }

    .button__icon {
        margin-right: $space-x-small;
    }
</style>
